import React, { useMemo } from 'react';
import './AttendanceLogs.css';
import { formatAttendanceTime } from '../utils/timeHelpers';
import { getAttendanceRemarks } from '../utils/attendanceHelpers';
import { filterAttendanceLogs } from '../utils/attendanceLogHelpers';
import { findEmployeeByUid } from '../utils/qrHelpers';
import { ACCOUNT_DREAME, ACCOUNT_MEGELIN } from '../constants/accountOptions';

const CalendarIcon = () => (
  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
  </svg>
);

const ACCOUNT_META = {
  [ACCOUNT_DREAME]: {
    title: 'Dreame Logs',
    badge: 'DREAME',
  },
  [ACCOUNT_MEGELIN]: {
    title: 'Megelin Logs',
    badge: 'MEGELIN',
  },
};

const AttendanceLogs = ({
  account,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  exportToPDF,
  exportToExcel,
  allLogs,
  registeredEmployees,
  personalMode = false,
  personalName = '',
}) => {
  const accountKey = account === ACCOUNT_MEGELIN ? ACCOUNT_MEGELIN : ACCOUNT_DREAME;
  const meta = personalMode
    ? { title: 'My Attendance Logs', badge: 'MY LOGS' }
    : ACCOUNT_META[accountKey];

  const filteredLogs = useMemo(() => (
    filterAttendanceLogs(allLogs, registeredEmployees, {
      startDate,
      endDate,
      account: personalMode ? null : accountKey,
    })
  ), [allLogs, registeredEmployees, startDate, endDate, accountKey, personalMode]);

  const getRemarkClass = (remarks) => {
    if (!remarks) return 'remark-pill--default';
    if (remarks.includes('LATE')) return 'remark-pill--late';
    if (remarks.includes('UNDERTIME')) return 'remark-pill--undertime';
    if (remarks === 'COMPLETED') return 'remark-pill--completed';
    return 'remark-pill--default';
  };

  return (
    <div className={`attendance-container${personalMode ? ' attendance-container--personal' : ''}`}>
      <div className={`attendance-header${personalMode ? ' attendance-header--personal' : ''}`}>
        <div className="attendance-header-left">
          <div className="attendance-badge">
            <span className="attendance-badge-dot" />
            {meta.badge}
          </div>
          <h2 className="attendance-title">{meta.title}</h2>
          <p className="attendance-subtitle">
            {personalMode && personalName ? `${personalName} · ` : ''}
            {filteredLogs.length} record{filteredLogs.length !== 1 ? 's' : ''} found
            {(startDate || endDate) ? ' for selected date range' : ''}
          </p>
        </div>

        <div className={`attendance-toolbar${personalMode ? ' attendance-toolbar--personal' : ''}`}>
          {personalMode ? (
            <div className="attendance-filter-group attendance-filter-group--personal">
              <label className="attendance-filter-label" htmlFor="attendance-date-start">
                Filter by Date
              </label>
              <div className="attendance-date-filter-bar">
                <div className="attendance-date-field">
                  <label className="attendance-date-field-label" htmlFor="attendance-date-start">From</label>
                  <input
                    id="attendance-date-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="date-input-glass"
                    aria-label="Start date"
                  />
                </div>
                <span className="date-separator" aria-hidden="true">—</span>
                <div className="attendance-date-field">
                  <label className="attendance-date-field-label" htmlFor="attendance-date-end">To</label>
                  <input
                    id="attendance-date-end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="date-input-glass"
                    aria-label="End date"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="attendance-filter-group">
              <label className="attendance-filter-label" htmlFor="attendance-date-start">
                Search Date
              </label>
              <div className="attendance-date-range">
                <div className="attendance-date-field">
                  <span className="attendance-date-field-label">From</span>
                  <input
                    id="attendance-date-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="date-input-glass"
                    aria-label="Start date"
                  />
                </div>
                <span className="date-separator" aria-hidden="true">—</span>
                <div className="attendance-date-field">
                  <span className="attendance-date-field-label">To</span>
                  <input
                    id="attendance-date-end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="date-input-glass"
                    aria-label="End date"
                  />
                </div>
                <span className="date-icon"><CalendarIcon /></span>
              </div>
            </div>
          )}

          {!personalMode && (
            <div className="attendance-actions">
              <button
                type="button"
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="btn-action btn-action--ghost"
              >
                Clear
              </button>
              <button type="button" onClick={() => exportToPDF(accountKey)} className="btn-action btn-action--primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                PDF Export
              </button>
              <button type="button" onClick={() => exportToExcel(accountKey)} className="btn-action btn-action--primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="16" y2="17" />
                </svg>
                Excel Export
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="attendance-table-wrap">
        {filteredLogs.length === 0 ? (
          <div className="attendance-empty">
            <div className="attendance-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <p className="attendance-empty-title">No attendance records</p>
            <p className="attendance-empty-sub">
              {startDate || endDate
                ? 'No logs found for the selected date range. Try adjusting your filters.'
                : personalMode
                  ? 'Your attendance records will appear here after you scan in or out.'
                  : `Records will appear here when ${meta.badge} employees scan their RFID or QR backup codes.`}
            </p>
          </div>
        ) : (
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>UID</th>
                <th>Position</th>
                <th>Date</th>
                <th>In</th>
                <th>Out</th>
                <th>Remarks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => {
                const emp = findEmployeeByUid(registeredEmployees, log.employeeId);
                const isOffDuty = log.timeOut && log.timeOut !== '' && log.timeOut !== '--:--';
                const displayStatus = isOffDuty ? 'OFF-DUTY' : 'ON-DUTY';
                const displayRemarks = getAttendanceRemarks(log, emp);
                const hasRemark = displayRemarks && displayRemarks.trim() !== '';

                return (
                  <tr key={log.id || index} className="table-row-hover">
                    <td>
                      <img
                        src={emp?.photo}
                        alt=""
                        className="attendance-photo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </td>
                    <td><span className="attendance-name">{emp?.name || 'Unknown'}</span></td>
                    <td><span className="attendance-muted">{log.employeeId}</span></td>
                    <td><span className="attendance-muted">{log.position}</span></td>
                    <td><span className="attendance-name">{log.date}</span></td>
                    <td><span className="attendance-time-in">{formatAttendanceTime(log.timeIn, emp?.shift, 'in')}</span></td>
                    <td><span className="attendance-time-out">{formatAttendanceTime(log.timeOut, emp?.shift, 'out')}</span></td>
                    <td>
                      {hasRemark ? (
                        <span className={`remark-pill ${getRemarkClass(displayRemarks)}`}>
                          {displayRemarks}
                        </span>
                      ) : (
                        <span className="attendance-empty-dash">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-pill ${isOffDuty ? 'status-pill--off' : 'status-pill--on'}`}>
                        {displayStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AttendanceLogs;
